import { useCallback, useState } from 'react';
import {
  UserPet,
  PetTemplate,
  PetInteractionLog,
} from '@typings/pets/pet.interface';
import { CollectionPath } from '@typings/collections.enum';
import { fuego } from '@nandorojo/swr-firestore';

type EvolutionStage = {
  stage: number;
  name: string;
  petTemplates: string[];
  requirements: { level: number; items?: string[] };
  weights?: Record<string, number>;
};
type EvolutionTree = {
  id: string;
  name: string;
  stages: EvolutionStage[];
  isActive?: boolean;
};

type EvoPrompt = null | {
  tree: EvolutionTree;
  currentStageIndex: number;
  nextStage: EvolutionStage;
  candidates: { id: string; weight: number; template?: PetTemplate }[];
};

type UseEvolutionArgs = {
  activePet: UserPet | undefined;
  petTemplates: PetTemplate[];
  petDocPath: string;
  setActivePetDoc: (data: Partial<UserPet>, opts?: any) => Promise<void>;
  currentUid: string | null;
  setUserPets: React.Dispatch<React.SetStateAction<UserPet[]>>;
  setInteractionLogs: React.Dispatch<React.SetStateAction<PetInteractionLog[]>>;
};

export function useEvolutionSystem({
  activePet,
  petTemplates,
  petDocPath,
  setActivePetDoc,
  currentUid,
  setUserPets,
  setInteractionLogs,
}: UseEvolutionArgs) {
  const [evoPrompt, setEvoPrompt] = useState<EvoPrompt>(null);

  const weightedPick = useCallback(
    (options: { id: string; weight: number }[]) => {
      const total = options.reduce(
        (acc, o) => acc + (Number(o.weight) || 0),
        0,
      );
      let r = Math.random() * (total || 1);
      for (const o of options) {
        r -= Number(o.weight) || 0;
        if (r <= 0) return o.id;
      }
      return options[options.length - 1]?.id;
    },
    [],
  );

  const findTreeAndStageForPet = useCallback(async () => {
    const evolutionsRef = `${CollectionPath.PETSETTING}/evolutions`;
    const evolutionsDoc = await fuego.db.doc(evolutionsRef).get().then((snap) => snap.data());

    if (!activePet) return null;
    const trees = (evolutionsDoc as any)?.trees as EvolutionTree[];
    if (!Array.isArray(trees) || trees.length === 0) return null;

    const petTemplateId = (activePet as any).templateId as string | undefined;
    if (!petTemplateId) return null;

    for (const tree of trees) {
      for (let i = 0; i < tree.stages.length; i++) {
        const st = tree.stages[i];
        if ((st.petTemplates || []).includes(petTemplateId)) {
          return { tree, currentStageIndex: i };
        }
      }
    }
    return null;
  }, [activePet]);

  const tryOpenEvolutionPrompt = useCallback(async () => {
    if (!activePet) return;
    const found = await findTreeAndStageForPet();
    if (!found) return;
    const { tree, currentStageIndex } = found;
    const nextStage = tree.stages[currentStageIndex + 1];
    if (!nextStage) return; // 마지막 단계

    // 레벨 조건 확인
    if ((activePet.level || 1) < (nextStage.requirements?.level || 1)) return;

    // 중복 프롬프트 방지
    const key = `evo_prompted_${activePet.id}_${currentStageIndex + 1}`;
    if (typeof window !== 'undefined' && localStorage.getItem(key) === '1')
      return;

    const weights = nextStage.weights || {};
    const candidates = (nextStage.petTemplates || []).map((id) => ({
      id,
      weight: (weights as any)[id] ?? 1,
      template: petTemplates.find((t) => t.id === id),
    }));
    if (candidates.length === 0) return;

    setEvoPrompt({
      tree: tree as any,
      currentStageIndex,
      nextStage,
      candidates,
    });
    if (typeof window !== 'undefined') localStorage.setItem(key, '1');
  }, [activePet, findTreeAndStageForPet, petTemplates]);

  const handleConfirmEvolution = useCallback(async () => {
    if (!activePet || !evoPrompt) return;
    const pickId = weightedPick(evoPrompt.candidates);
    const chosen = petTemplates.find((t) => t.id === pickId);
    if (!chosen) {
      setEvoPrompt(null);
      return;
    }

    // 필드명 정합성 유지: customImgURL, petTemplateId 사용
    const patch: Partial<UserPet> = {
      ...(chosen.imgURL ? ({ customImgURL: chosen.imgURL } as any) : {}),
      ...(chosen.id ? ({ petTemplateId: chosen.id } as any) : {}),
      experience: 0,
      updatedAt: new Date() as any,
    } as any;

    try {
      // legacy 이미지 보존 및 안전 업데이트 (트랜잭션)
      await fuego.db.runTransaction(async (tx: any) => {
        const ref: any = fuego.db.doc(petDocPath);
        const snap = await tx.get(ref);
        const curr = (snap?.data?.() || {}) as any;
        const prevURL = curr.customImgURL || '';
        const legacyEntry = prevURL ? { levelKey: `level${curr.level || 1}`, imgURL: prevURL } : null;
        const updateData: any = { ...patch };
        if (legacyEntry) {
          const currList = Array.isArray(curr.legacyPetImgList) ? curr.legacyPetImgList : [];
          updateData.legacyPetImgList = [...currList, legacyEntry];
        }
        tx.update(ref, updateData);
      });

      // 로컬 상태 반영
      setUserPets((prev) => prev.map((p) => (p.id === activePet.id ? { ...p, ...patch } : p)));

      // 로그 추가 (비차단)
      try {
        const evoLog: PetInteractionLog = {
          id: `evo_${Date.now()}`,
          userId: currentUid || 'guest',
          petId: activePet.id,
          type: 'evolution',
          message: `${activePet.customName}이(가) 진화했습니다!`,
          createdAt: new Date(),
        } as any;
        if (currentUid) {
          await fuego.db
            .collection(`${CollectionPath.USERS}/${currentUid}/pets/${activePet.id}/logs`)
            .add(evoLog as any);
        }
        setInteractionLogs((prev) => [evoLog, ...prev]);
      } catch { }
    } catch (e) {
      console.warn('진화 적용 실패', e);
    } finally {
      setEvoPrompt(null);
    }
  }, [
    activePet,
    evoPrompt,
    petDocPath,
    setActivePetDoc,
    petTemplates,
    currentUid,
    setUserPets,
    setInteractionLogs,
    weightedPick,
  ]);

  const handleCancelEvolution = useCallback(() => {
    setEvoPrompt(null);
  }, []);

  return {
    evoPrompt,
    tryOpenEvolutionPrompt,
    handleConfirmEvolution,
    handleCancelEvolution,
  };
}

export default useEvolutionSystem;
